import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Download, CreditCard, ShieldCheck, Video, Link as LinkIcon, FileText, Radio, Calendar, Rocket } from "lucide-react";
import EventsPanel from "./EventsPanel";
import StudentPonderrLearn from "./ponderr_learn/StudentPonderrLearn";
import { KitStorePanel } from "./student_store/StudentKitStore";
import { PonderrTVPanel } from "./student_tv/StudentPonderrTV";
import AlertsInbox from "./shared/AlertsInbox";

const TABS = [
  {id:"attendance", label:"My Attendance"},
  {id:"marks", label:"Marks Cards"},
  {id:"reports", label:"Report Cards",
    disabled: true, title: "Consolidated under Marks Cards"},
  {id:"events", label:"Events"},
  {id:"fees", label:"Fees"},
  {id:"timetable", label:"Time Table"},
  {id:"learnings", label:"Learnings"},
  {id:"xplore", label:"Elevate"},
  {id:"ponderr", label:"Ponderr"},
  {id:"kit_store", label:"Kit Store"},
  {id:"ponderr_learn", label:"PonderrLearn"},
  {id:"ponderr_tv", label:"PonderrTV"},
  {id:"alerts", label:"Alerts"},
  {id:"parent", label:"Parent Access"},
];

function ComingSoon({ title, blurb, tone = "bg-[#FDE047]" }) {
  return (
    <div className={`nb-card p-8 ${tone} space-y-3`}>
      <span className="nb-badge bg-white text-xs">Coming Soon</span>
      <h2 className="display text-3xl font-bold">{title}</h2>
      <p className="text-sm max-w-2xl">{blurb}</p>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("attendance");
  const [gate, setGate] = useState({ gated: false });
  const studentId = user?.linked_student_id;
  useEffect(() => { api.get("/waba/my-gate").then(r => setGate(r.data)).catch(() => {}); }, []);
  const tabs = TABS.map(t => t.id === "marks" && gate.gated
    ? { ...t, label: "🔒 Marks Cards", title: `Locked — clear dues of ₹${gate.outstanding} to unlock` }
    : t);
  const gatedContent = (
    <div className="nb-card p-8 bg-[#FCA5A5] space-y-2" data-testid="marks-gated-lock">
      <span className="nb-badge bg-white text-xs">🔒 Locked</span>
      <h2 className="display text-2xl font-bold">Marks & Report Cards are locked</h2>
      <p className="text-sm">Outstanding fees of <b>₹{gate.outstanding}</b> exceed the school's access threshold. Please clear your dues to unlock your Marks Cards and Report Cards.</p>
    </div>
  );
  return (
    <Shell title="Student Console" subtitle={user?.name} tabs={tabs} active={tab} onTab={setTab}>
      {!studentId && <div className="nb-card p-6 bg-[#FCA5A5]">Your account isn&apos;t linked to a student record. Please contact admin.</div>}
      {studentId && tab==="attendance" && <Att studentId={studentId}/>}
      {studentId && tab==="marks" && (gate.gated ? gatedContent : <Marks studentId={studentId}/>)}
      {studentId && tab==="reports" && <Reports studentId={studentId}/>}
      {tab==="events" && <EventsPanel mode="view"/>}
      {studentId && tab==="fees" && <Fees studentId={studentId}/>}
      {studentId && tab==="timetable" && <Timetable/>}
      {studentId && tab==="learnings" && <Learnings/>}
      {studentId && tab==="xplore" && <Xplore/>}
      {studentId && tab==="ponderr" && <Ponderr/>}
      {studentId && tab==="kit_store" && <KitStorePanel/>}
      {studentId && tab==="ponderr_learn" && <StudentPonderrLearn/>}
      {studentId && tab==="ponderr_tv" && <PonderrTVPanel/>}
      {studentId && tab==="alerts" && <AlertsInbox/>}
      {studentId && tab==="parent" && <Parent studentId={studentId}/>}
    </Shell>
  );
}

const XKIND_BG = {
  "Reading Progress": "bg-[#93C5FD]",
  "Live Teams Webinar": "bg-[#FCA5A5]",
  "Math Progress": "bg-[#86EFAC]",
  "MineCraft": "bg-[#22C55E]",
  "Makecode Arcade": "bg-[#FDE047]",
  "OneNote Notebook": "bg-[#C4B5FD]",
  "SharePoint site": "bg-[#93C5FD]",
};

function Xplore() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/student/xplore").then(r => setRows(r.data)); }, []);
  const now = Date.now();
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#FDE047] flex items-center gap-2">
        <Rocket size={20}/>
        <div>
          <div className="display text-xl font-bold">Elevate Assignments</div>
          <p className="text-sm">Microsoft Education activities from your school platform.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(x => {
          const overdue = x.due_at && new Date(x.due_at).getTime() < now;
          return (
            <div key={x.assignment_id} className={`nb-card p-4 ${XKIND_BG[x.category] || "bg-white"}`}
                 data-testid={`stu-xplore-${x.assignment_id}`}>
              <div className="flex items-center justify-between">
                <span className="nb-label">{x.category}</span>
                {overdue && <span className="nb-badge bg-[#FCA5A5]">OVERDUE</span>}
              </div>
              <h4 className="display text-lg font-bold mt-1">{x.title}</h4>
              {x.description && <p className="text-sm mt-2">{x.description}</p>}
              {x.due_at && (
                <div className="text-xs mono mt-2">
                  Due: {new Date(x.due_at).toLocaleString()}
                </div>
              )}
              {x.url && (
                <a href={x.url} target="_blank" rel="noreferrer"
                   className="nb-btn nb-btn-secondary mt-3 inline-flex text-xs">
                  Start <LinkIcon size={12}/>
                </a>
              )}
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="nb-card p-6 text-slate-500 col-span-full text-sm">
            No Elevate assignments yet. The Global Admin will push Microsoft Education activities here.
          </div>
        )}
      </div>
    </div>
  );
}

const LKIND = {
  video: {n:"Video", Icon:Video, bg:"bg-[#93C5FD]"},
  live:  {n:"Live Class", Icon:Radio, bg:"bg-[#FCA5A5]"},
  link:  {n:"Link / Course", Icon:LinkIcon, bg:"bg-[#86EFAC]"},
  note:  {n:"Note", Icon:FileText, bg:"bg-[#FDE047]"},
};

function Learnings() {
  const [rows, setRows] = useState([]);
  const [activeSubject, setActiveSubject] = useState("all");
  useEffect(() => { api.get("/student/learnings").then(r => setRows(r.data)); }, []);
  // Iter30 — build subject sub-tabs from resources actually shared with this student
  const subjects = Array.from(new Set(rows.map(r => r.subject).filter(Boolean))).sort();
  const filtered = activeSubject === "all" ? rows : rows.filter(r => r.subject === activeSubject);
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#C4B5FD]">
        <div className="display text-xl font-bold">Learnings from your teachers</div>
        <p className="text-sm mt-1">Videos, live classes, notes and course links published by your class educators.</p>
      </div>
      {subjects.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="stu-learnings-subject-tabs">
          <button onClick={()=>setActiveSubject("all")}
                  className={`nb-btn text-xs whitespace-nowrap ${activeSubject==="all" ? "" : "nb-btn-ghost"}`}
                  data-testid="stu-learnings-tab-all">All ({rows.length})</button>
          {subjects.map(s => {
            const count = rows.filter(r => r.subject === s).length;
            return (
              <button key={s} onClick={()=>setActiveSubject(s)}
                      className={`nb-btn text-xs whitespace-nowrap ${activeSubject===s ? "" : "nb-btn-ghost"}`}
                      data-testid={`stu-learnings-tab-${s}`}>
                {s} ({count})
              </button>
            );
          })}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(x => {
          const K = LKIND[x.kind] || LKIND.link;
          return (
            <div key={x.learning_id} className={`nb-card p-4 ${K.bg}`}
                 data-testid={`stu-lrn-${x.learning_id}`}>
              <div className="flex items-center gap-2"><K.Icon size={16}/>
                <span className="nb-label">{K.n}</span>
                {x.subject && <span className="nb-badge bg-white ml-auto">{x.subject}</span>}
              </div>
              <h4 className="display text-lg font-bold mt-1">{x.title}</h4>
              <div className="text-xs mt-1 text-slate-700">
                by <b>{x.educator_name}</b> {x.educator_subject && `· ${x.educator_subject}`}
              </div>
              {x.description && <p className="text-sm mt-2">{x.description}</p>}
              {x.url && (
                <a href={x.url} target="_blank" rel="noreferrer"
                   className="nb-btn nb-btn-secondary mt-3 inline-flex text-xs">
                  Open <LinkIcon size={12}/>
                </a>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="nb-card p-6 text-slate-500 col-span-full">
            Nothing shared yet. Your teachers will publish resources here.
          </div>
        )}
      </div>
    </div>
  );
}

function Timetable() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/timetables/mine").then(r => setRows(r.data)); }, []);
  const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat"];
  const periods = Array.from(new Set(rows.map(r => r.period))).sort((a,b) => a - b);
  const grid = {};
  rows.forEach(r => { grid[`${r.day_of_week}|${r.period}`] = r; });
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#93C5FD] flex items-center gap-2">
        <Calendar size={20}/>
        <div>
          <div className="display text-xl font-bold">My Class Time Table</div>
          <p className="text-sm">Designed by your school admin.</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="nb-card p-6 text-slate-500">Time table not yet published for your class.</div>
      ) : (
        <div className="nb-card p-0 overflow-x-auto">
          <table className="nb-table">
            <thead><tr><th>Period</th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr></thead>
            <tbody>
              {periods.map(p => (
                <tr key={p}>
                  <td className="font-bold mono">P{p}</td>
                  {DAYS.map(d => {
                    const s = grid[`${d}|${p}`];
                    return <td key={d} className={s ? "bg-[#FDE047]" : ""}>
                      {s ? (
                        <div>
                          <div className="font-bold text-sm">{s.subject}</div>
                          {s.educator_name && <div className="text-xs">{s.educator_name}</div>}
                          {s.start_time && <div className="text-xs mono">{s.start_time}-{s.end_time}</div>}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export function Ponderr() {
  // Iter28 — Curriculum-driven: school.board_code + student.grade → subjects → chapters → lessons
  // Iter29 — chapter exams + attempts + certificate
  const [meta, setMeta] = useState({board_code:null, grade:null, subjects:[], reason:""});
  const [activeSubject, setActiveSubject] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [openChapter, setOpenChapter] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [educatorNotes, setEducatorNotes] = useState([]);   // Iter29 — for lessons
  const [activeLesson, setActiveLesson] = useState(null);
  const [chapterExams, setChapterExams] = useState([]);
  const [subjectExams, setSubjectExams] = useState([]);
  const [activeAttempt, setActiveAttempt] = useState(null); // {attempt_id, exam, questions, answers}
  const [attemptResult, setAttemptResult] = useState(null); // {score_pct, passed, needs_review, attempt_id}

  useEffect(()=>{
    api.get("/student/lms/subjects").then(r=>setMeta(r.data))
       .catch(()=>setMeta({board_code:null, grade:null, subjects:[], reason:"Failed to load"}));
  }, []);

  const openSubject = async (s) => {
    setActiveSubject(s); setOpenChapter(null); setLessons([]); setActiveLesson(null);
    setChapterExams([]); setSubjectExams([]); setAttemptResult(null);
    try {
      const [c, ex] = await Promise.all([
        api.get(`/student/lms/curriculum/${s.curriculum_id}`),
        api.get(`/student/lms/exams`, {params:{subject_id: s.subject_id}}),
      ]);
      setCurriculum(c.data);
      setSubjectExams(ex.data.filter(e => e.scope === "subject"));
    } catch { toast.error("Failed to load curriculum"); }
  };

  const openChapterHandler = async (ch) => {
    setOpenChapter(ch); setActiveLesson(null); setAttemptResult(null);
    try {
      const [ls, exs] = await Promise.all([
        api.get(`/student/lms/lessons/${ch.chapter_id}`),
        api.get(`/student/lms/exams`, {params:{chapter_id: ch.chapter_id}}),
      ]);
      setLessons(ls.data);
      setChapterExams(exs.data);
    } catch { toast.error("Failed to load lessons"); }
  };

  const openLesson = async (l) => {
    setActiveLesson(l);
    try {
      const r = await api.get(`/student/lms/educator-notes/${l.lesson_id}`);
      setEducatorNotes(r.data || []);
    } catch { setEducatorNotes([]); }
  };

  const startExam = async (ex) => {
    try {
      const r = await api.post("/student/lms/attempts", { exam_id: ex.exam_id });
      setActiveAttempt({...r.data, answers: {}});
      setAttemptResult(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to start"); }
  };

  const backToSubjects = () => { setActiveSubject(null); setCurriculum(null); setOpenChapter(null); setLessons([]); setActiveLesson(null); setChapterExams([]); setSubjectExams([]); setAttemptResult(null); };
  const backToChapters = () => { setOpenChapter(null); setLessons([]); setActiveLesson(null); setChapterExams([]); setAttemptResult(null); };

  if (!meta.board_code || !meta.grade) {
    return (
      <div className="nb-card p-6 bg-[#FDE047] space-y-2">
        <div className="display text-xl font-bold">Ponderr LMS</div>
        <div className="text-sm">Your Ponderr library isn&apos;t ready yet.</div>
        <div className="text-xs text-slate-700">{meta.reason || "Your school administrator needs to set the academic Board, and your grade needs to be assigned."}</div>
      </div>
    );
  }

  // ---- EXAM RUNNER ----
  if (activeAttempt) {
    return (
      <ExamRunner attempt={activeAttempt}
                  onSubmitted={(res)=>{ setActiveAttempt(null); setAttemptResult(res);
                    // refresh exam lists
                    if (openChapter) openChapterHandler(openChapter);
                    else if (activeSubject) openSubject(activeSubject);
                  }}
                  onCancel={()=>setActiveAttempt(null)}/>
    );
  }

  // ---- LESSON VIEW ----
  if (activeLesson) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2 items-center text-xs mono">
          <button onClick={backToSubjects} className="underline">Subjects</button>
          <span>›</span>
          <button onClick={()=>{ setActiveLesson(null); }} className="underline">{activeSubject.subject_name}</button>
          <span>›</span>
          <span>{openChapter.title}</span>
        </div>
        <LessonViewer lesson={activeLesson}/>
        {educatorNotes.length > 0 && (
          <div className="nb-card p-4 bg-[#FDE047] space-y-3">
            <div className="nb-label">Notes from your Educators</div>
            {educatorNotes.map(n => (
              <div key={n.note_id} className="nb-card-sm bg-white p-3">
                <div className="text-xs mono mb-1">— {n.educator_name}</div>
                <div className="prose max-w-none text-sm"
                     dangerouslySetInnerHTML={{__html: n.notes_html}}/>
              </div>
            ))}
          </div>
        )}
        <button onClick={()=>setActiveLesson(null)} className="nb-btn nb-btn-ghost">← Back to lessons</button>
      </div>
    );
  }

  // ---- CHAPTER (LESSON LIST) VIEW ----
  if (openChapter) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2 items-center text-xs mono">
          <button onClick={backToSubjects} className="underline">Subjects</button>
          <span>›</span>
          <button onClick={backToChapters} className="underline">{activeSubject.subject_name}</button>
          <span>›</span>
          <span>{openChapter.title}</span>
        </div>
        <div className="nb-card p-4 bg-[#93C5FD]">
          <div className="nb-label">Chapter {openChapter.order}</div>
          <h3 className="display text-xl font-bold">{openChapter.title}</h3>
          {openChapter.description && <p className="text-sm mt-1">{openChapter.description}</p>}
        </div>

        {attemptResult && (
          <AttemptResultBanner res={attemptResult} onClose={()=>setAttemptResult(null)}/>
        )}

        {lessons.length === 0 ? (
          <div className="nb-card p-6 text-center text-slate-500 text-sm">
            No lessons yet — check back soon.
          </div>
        ) : (
          <div className="space-y-2">
            {lessons.map(l => (
              <button key={l.lesson_id} onClick={()=>openLesson(l)}
                      className="nb-card w-full p-3 text-left flex items-center justify-between hover:bg-[#FDE047]"
                      data-testid={`ponderr-lesson-${l.lesson_id}`}>
                <div>
                  <span className="nb-badge bg-white text-xs mr-2">{LESSON_KIND_LABEL(l.kind)}</span>
                  <span className="font-bold">{l.order}. {l.title}</span>
                  {l.description && <div className="text-xs text-slate-600 mt-1">{l.description}</div>}
                </div>
                {l.duration_min ? <span className="text-xs mono">{l.duration_min} min</span> : null}
              </button>
            ))}
          </div>
        )}

        {chapterExams.length > 0 && (
          <div className="nb-card p-4 bg-[#C4B5FD] space-y-2">
            <div className="display text-lg font-bold">Chapter Assessments</div>
            <ExamList exams={chapterExams} onStart={startExam}/>
          </div>
        )}

        <button onClick={backToChapters} className="nb-btn nb-btn-ghost">← Back to chapters</button>
      </div>
    );
  }

  // ---- SUBJECT (CHAPTER LIST) VIEW ----
  if (activeSubject && curriculum) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2 items-center text-xs mono">
          <button onClick={backToSubjects} className="underline">Subjects</button>
          <span>›</span>
          <span>{activeSubject.subject_name}</span>
        </div>
        <div className="nb-card p-4 bg-[#86EFAC]">
          <div className="nb-label">{meta.board_code} · {meta.grade}</div>
          <h3 className="display text-2xl font-bold">{activeSubject.subject_name}</h3>
          <div className="text-xs mt-1">{curriculum.chapters?.length || 0} chapter(s)</div>
        </div>

        {attemptResult && (
          <AttemptResultBanner res={attemptResult} onClose={()=>setAttemptResult(null)}/>
        )}

        {(curriculum.chapters||[]).length === 0 ? (
          <div className="nb-card p-6 text-center text-slate-500 text-sm">
            No chapters published yet for this subject.
          </div>
        ) : (
          <div className="space-y-2">
            {curriculum.chapters.map(ch => (
              <button key={ch.chapter_id} onClick={()=>openChapterHandler(ch)}
                      className="nb-card w-full p-3 text-left hover:bg-[#FDE047]"
                      data-testid={`ponderr-chapter-${ch.chapter_id}`}>
                <div className="font-bold">{ch.order}. {ch.title}</div>
                {ch.description && <div className="text-xs text-slate-600 mt-1">{ch.description}</div>}
              </button>
            ))}
          </div>
        )}

        {subjectExams.length > 0 && (
          <div className="nb-card p-4 bg-[#FDE047] space-y-2">
            <div className="display text-lg font-bold">Subject Exams</div>
            <ExamList exams={subjectExams} onStart={startExam}/>
          </div>
        )}

        <button onClick={backToSubjects} className="nb-btn nb-btn-ghost">← Back to subjects</button>
      </div>
    );
  }

  // ---- SUBJECT GRID (default) ----
  return (
    <div className="space-y-5">
      <div className="nb-card p-4 bg-[#C4B5FD]">
        <div className="nb-label">Ponderr LMS · {meta.board_code} · {meta.grade}</div>
        <div className="display text-2xl font-bold">Your Subjects</div>
        <div className="text-xs mt-1">Curriculum curated by Ponderr and mapped to your school&apos;s board &amp; your grade.</div>
      </div>
      <MyAttemptsBar/>
      {meta.subjects.length === 0 ? (
        <div className="nb-card p-6 text-center text-slate-500 text-sm">
          No subjects published yet for {meta.board_code} · {meta.grade}. Ponderr is curating your library.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {meta.subjects.map(s => (
            <button key={s.curriculum_id} onClick={()=>openSubject(s)}
                    className="nb-card p-4 text-left hover:bg-[#FDE047]"
                    data-testid={`ponderr-subject-${s.subject_code}`}>
              <div className="nb-label">{s.subject_code}</div>
              <div className="display text-lg font-bold mt-1">{s.subject_name}</div>
              <div className="text-xs mt-2 text-slate-600">{s.chapter_count} chapter(s)</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ExamList({ exams, onStart }) {
  return (
    <div className="space-y-2">
      {exams.map(ex => {
        const canRetake = !ex.max_attempts || (ex.attempts_used < ex.max_attempts);
        return (
          <div key={ex.exam_id} className="nb-card-sm p-3 bg-white flex flex-wrap items-center justify-between gap-2"
               data-testid={`ponderr-exam-${ex.exam_id}`}>
            <div className="min-w-0">
              <div className="font-bold">{ex.title}</div>
              <div className="text-xs mono text-slate-600">
                {ex.question_count} q · pass {ex.passing_pct}% · attempts {ex.attempts_used}/{ex.max_attempts || "∞"}
                {ex.best_score != null ? ` · best ${ex.best_score}%` : ""}
                {ex.ever_passed ? " · ✓ passed" : ""}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {ex.ever_passed && <CertificateDownloadHelper exam={ex}/>}
              <button onClick={()=>onStart(ex)} disabled={!canRetake}
                      className="nb-btn"
                      data-testid={`ponderr-start-${ex.exam_id}`}>
                {ex.attempts_used > 0 ? "Retake" : "Start"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CertificateDownloadHelper({ exam }) {
  const download = async () => {
    try {
      // Fetch latest passed attempt
      const r = await api.get("/student/lms/attempts", {params:{exam_id: exam.exam_id}});
      const passed = r.data.filter(a => a.passed);
      if (!passed.length) return toast.error("No passed attempts");
      const best = passed.sort((a,b)=>(b.score_pct||0)-(a.score_pct||0))[0];
      const dl = await api.get(`/student/lms/certificate/${best.attempt_id}`, { responseType:"blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Certificate_${exam.title.replace(/\W+/g,"_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };
  return (
    <button onClick={download} className="nb-btn nb-btn-secondary text-xs"
            data-testid={`ponderr-cert-${exam.exam_id}`}>
      ⬇ Certificate
    </button>
  );
}

function AttemptResultBanner({ res, onClose }) {
  const passed = res.passed;
  return (
    <div className={`nb-card p-4 ${passed ? "bg-[#86EFAC]" : res.needs_review ? "bg-[#FDE047]" : "bg-[#FCA5A5]"}`}
         data-testid="ponderr-result-banner">
      <div className="flex justify-between">
        <div>
          <div className="display text-xl font-bold">
            {passed ? "Passed!" : res.needs_review ? "Submitted — pending educator review" : "Not passed yet"}
          </div>
          <div className="text-sm mt-1">Score: <b>{res.score_pct}%</b> ({res.scored_marks}/{res.total_marks})</div>
        </div>
        <button onClick={onClose} className="nb-btn nb-btn-ghost">×</button>
      </div>
    </div>
  );
}

function MyAttemptsBar() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/student/lms/attempts").then(r=>setRows(r.data)); }, []);
  if (!rows.length) return null;
  const passed = rows.filter(r => r.passed);
  return (
    <div className="nb-card p-3 bg-[#93C5FD] text-xs mono flex flex-wrap gap-3 items-center">
      <span>My Assessments:</span>
      <span>Total: <b>{rows.length}</b></span>
      <span>Passed: <b className="text-emerald-800">{passed.length}</b></span>
      <span>Certificates: <b>{passed.length}</b></span>
    </div>
  );
}

function ExamRunner({ attempt, onSubmitted, onCancel }) {
  const [ans, setAns] = useState({}); // {question_id: {selected_indices|selected_bool|text_answer}}
  const [submitting, setSubmitting] = useState(false);

  const setSel = (qid, patch) => setAns(a => ({...a, [qid]: {...(a[qid]||{}), ...patch}}));

  const submit = async () => {
    if (submitting) return;
    if (!window.confirm("Submit your answers? This cannot be undone.")) return;
    setSubmitting(true);
    try {
      const answers = attempt.questions.map(q => ({
        question_id: q.question_id,
        selected_indices: ans[q.question_id]?.selected_indices || [],
        selected_bool: ans[q.question_id]?.selected_bool ?? null,
        text_answer: ans[q.question_id]?.text_answer || "",
      }));
      const r = await api.post(`/student/lms/attempts/${attempt.attempt_id}/submit`, { answers });
      onSubmitted(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="ponderr-exam-runner">
      <div className="nb-card p-4 bg-[#FDE047] flex justify-between items-start">
        <div>
          <div className="nb-label">Exam</div>
          <div className="display text-xl font-bold">{attempt.exam.title}</div>
          {attempt.exam.description && <div className="text-xs mt-1">{attempt.exam.description}</div>}
          <div className="text-xs mono mt-1">Pass {attempt.exam.passing_pct}%
            {attempt.exam.duration_min ? ` · ${attempt.exam.duration_min}m` : ""}
            · {attempt.questions.length} questions</div>
        </div>
        <button onClick={onCancel} className="nb-btn nb-btn-ghost">Abort</button>
      </div>

      {attempt.questions.map((q, i) => (
        <div key={q.question_id} className="nb-card p-4 bg-white space-y-2"
             data-testid={`ponderr-question-${q.question_id}`}>
          <div className="flex gap-2 items-center">
            <span className="nb-badge bg-slate-200 text-xs">Q{i+1}</span>
            <span className="nb-badge bg-[#C4B5FD] text-xs">{q.marks} mark{q.marks>1?"s":""}</span>
          </div>
          <div className="prose max-w-none text-sm"
               dangerouslySetInnerHTML={{__html: q.question_html}}/>

          {(q.kind==="mcq_single" || q.kind==="mcq_multi") && (
            <div className="space-y-1 mt-2">
              {(q.options||[]).map((opt, oi) => {
                const isMulti = q.kind==="mcq_multi";
                const sel = ans[q.question_id]?.selected_indices || [];
                const isSel = sel.includes(oi);
                const onClick = () => {
                  if (isMulti) {
                    setSel(q.question_id, {selected_indices: isSel ? sel.filter(x=>x!==oi) : [...sel, oi]});
                  } else {
                    setSel(q.question_id, {selected_indices: [oi]});
                  }
                };
                return (
                  <label key={oi}
                         className={`flex gap-2 items-center p-2 border-2 rounded cursor-pointer ${isSel ? "bg-[#FDE047] border-black" : "border-slate-200"}`}
                         data-testid={`ponderr-opt-${q.question_id}-${oi}`}>
                    <input type={isMulti ? "checkbox" : "radio"} checked={isSel}
                           onChange={onClick} name={`q-${q.question_id}`}/>
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          )}
          {q.kind==="true_false" && (
            <div className="flex gap-2">
              <button type="button" onClick={()=>setSel(q.question_id, {selected_bool:true})}
                      className={`nb-btn ${ans[q.question_id]?.selected_bool===true ? "bg-[#86EFAC]" : "nb-btn-ghost"}`}
                      data-testid={`ponderr-tf-true-${q.question_id}`}>True</button>
              <button type="button" onClick={()=>setSel(q.question_id, {selected_bool:false})}
                      className={`nb-btn ${ans[q.question_id]?.selected_bool===false ? "bg-[#FCA5A5]" : "nb-btn-ghost"}`}
                      data-testid={`ponderr-tf-false-${q.question_id}`}>False</button>
            </div>
          )}
          {q.kind==="short_answer" && (
            <textarea className="nb-textarea" rows={3}
                      placeholder="Type your answer…"
                      value={ans[q.question_id]?.text_answer || ""}
                      onChange={e=>setSel(q.question_id, {text_answer: e.target.value})}
                      data-testid={`ponderr-short-${q.question_id}`}/>
          )}
        </div>
      ))}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="nb-btn nb-btn-ghost">Cancel</button>
        <button onClick={submit} disabled={submitting} className="nb-btn"
                data-testid="ponderr-submit-attempt">
          {submitting ? "Submitting…" : "Submit Attempt"}
        </button>
      </div>
    </div>
  );
}

function LESSON_KIND_LABEL(k) {
  return ({
    notes:"Notes", pdf:"PDF", audio:"Audio",
    video_upload:"Video", video_youtube:"YouTube", html:"Interactive",
  })[k] || k;
}

function LessonViewer({ lesson }) {
  const [blobUrl, setBlobUrl] = useState("");
  useEffect(()=>{
    let revoke;
    if ((lesson.kind === "pdf" || lesson.kind === "audio" || lesson.kind === "video_upload") && lesson.storage_path) {
      (async () => {
        try {
          const r = await api.get(`/lms/files/serve/${lesson.storage_path}`, { responseType:"blob" });
          const url = URL.createObjectURL(r.data);
          setBlobUrl(url); revoke = url;
        } catch (e) { /* ignore */ }
      })();
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [lesson]);

  const ytEmbed = (url) => {
    if (!url) return "";
    // Handle youtu.be, watch?v=, and full embed forms
    let m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  };

  return (
    <div className="nb-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="nb-badge bg-[#FDE047] text-xs">{LESSON_KIND_LABEL(lesson.kind)}</span>
        <h4 className="display text-xl font-bold">{lesson.title}</h4>
      </div>
      {lesson.description && <p className="text-sm text-slate-700">{lesson.description}</p>}

      {lesson.kind === "notes" && (
        <div className="prose max-w-none text-sm"
             dangerouslySetInnerHTML={{__html: lesson.notes_html || "<i>No content.</i>"}}/>
      )}
      {lesson.kind === "html" && (
        <iframe title={lesson.title}
                srcDoc={lesson.html_content}
                sandbox="allow-scripts allow-same-origin"
                className="w-full rounded border-2 border-black"
                style={{height:"70vh"}}/>
      )}
      {lesson.kind === "pdf" && blobUrl && (
        <iframe title={lesson.title} src={blobUrl}
                className="w-full rounded border-2 border-black"
                style={{height:"75vh"}}/>
      )}
      {lesson.kind === "audio" && blobUrl && (
        <audio src={blobUrl} controls className="w-full"/>
      )}
      {lesson.kind === "video_upload" && blobUrl && (
        <video src={blobUrl} controls className="w-full rounded border-2 border-black"
               style={{maxHeight:"70vh"}}/>
      )}
      {lesson.kind === "video_youtube" && lesson.youtube_url && (
        <div className="relative w-full" style={{paddingBottom:"56.25%"}}>
          <iframe title={lesson.title} src={ytEmbed(lesson.youtube_url)}
                  className="absolute inset-0 w-full h-full rounded border-2 border-black"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen/>
        </div>
      )}
      {(lesson.kind === "pdf" || lesson.kind === "audio" || lesson.kind === "video_upload") && !blobUrl && lesson.storage_path && (
        <div className="text-xs text-slate-500">Loading…</div>
      )}
    </div>
  );
}

function Reports({ studentId }) {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get(`/marks-cards/published/${studentId}`).then(r=>setRows(r.data)); }, [studentId]);
  const dl = async (pubId) => {
    const r = await api.get(`/marks-cards/published-pdf/${pubId}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return (
    <div className="space-y-5">
      <div className="nb-card p-4 bg-[#FDE047] text-sm">
        Report cards published by your school administrator appear here. Parents can access via the Parent Portal too.
      </div>
      <div className="nb-card p-0 overflow-x-auto">
        <table className="nb-table">
          <thead><tr><th>Test Type</th><th>Published</th><th>Subjects</th><th>Action</th></tr></thead>
          <tbody>
            {rows.map(p=>(
              <tr key={p.publication_id} data-testid={`report-${p.publication_id}`}>
                <td className="font-bold">{p.test_type}</td>
                <td className="mono text-xs">{p.published_at?.slice(0,10)}</td>
                <td className="text-sm">{(p.marks||[]).map(m=>m.subject).join(", ")||"—"}</td>
                <td><button onClick={()=>dl(p.publication_id)} className="nb-btn"><Download size={14}/>PDF</button></td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No report cards published yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Att({ studentId }) {
  const [d, setD] = useState(null);
  useEffect(()=>{ api.get(`/attendance/student/${studentId}`).then(r=>setD(r.data)); }, [studentId]);
  if (!d) return <div className="mono">Loading…</div>;
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-5">
        <div className="nb-card p-6 bg-[#86EFAC]"><div className="nb-label">Present</div><div className="display text-4xl font-black">{d.present}</div></div>
        <div className="nb-card p-6 bg-[#FCA5A5]"><div className="nb-label">Total Days</div><div className="display text-4xl font-black">{d.total}</div></div>
        <div className="nb-card p-6 bg-[#FDE047]"><div className="nb-label">Percentage</div><div className="display text-4xl font-black">{d.percentage}%</div></div>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {d.history.map(h => (
              <tr key={h.date}><td className="mono">{h.date}</td>
                <td><span className={`nb-badge ${h.present?"bg-[#86EFAC]":"bg-[#FCA5A5]"}`}>{h.present?"PRESENT":"ABSENT"}</span></td></tr>
            ))}
            {d.history.length===0 && <tr><td colSpan={2} className="text-center py-6 text-slate-500">No attendance yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Marks({ studentId }) {
  const [rows, setRows] = useState([]);
  // Iter49 — Test Type filter (pulls from School Admin → Settings → School Profile → test_types)
  const [testTypes, setTestTypes] = useState([]);
  const [testFilter, setTestFilter] = useState("");
  useEffect(()=>{
    api.get(`/marks/student/${studentId}`).then(r=>setRows(r.data));
    api.get("/settings").then(r => setTestTypes(r.data?.test_types || []))
                        .catch(()=>setTestTypes([]));
  }, [studentId]);
  const dl = async () => {
    // Unified engine: same renderer/flags as the educator's card (split tables +
    // scholastic-only summary + cycle attendance). Requires a test type.
    const tt = testFilter || (rows[0] && rows[0].exam) || "";
    if (!tt) { toast.error("No marks available to print yet"); return; }
    try {
      const r = await api.get(`/marks-card/${studentId}`, {
        params: { test_type: tt }, responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to build Marks Card"); }
  };
  // Iter49 — Individual comparative multi-test Report Card (same shape as bulk generator)
  const dlReport = async () => {
    try {
      const r = await api.post("/report-cards/generate",
        { student_id: studentId }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ReportCard_${studentId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report Card downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to build Report Card"); }
  };
  const filteredRows = testFilter ? rows.filter(m => m.exam === testFilter) : rows;
  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="display text-2xl font-bold">Marks Cards</h2>
        <div className="flex gap-2 items-center flex-wrap">
          {/* Iter49 — Test Type filter dropdown */}
          <label className="flex items-center gap-2 text-xs">
            Filter by Test Type:
            <select className="nb-select text-xs" value={testFilter}
                    onChange={e=>setTestFilter(e.target.value)}
                    data-testid="stu-marks-test-filter">
              <option value="">All test types</option>
              {testTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button data-testid="download-marks-card" onClick={dl} className="nb-btn">
            <Download size={16}/>Download Marks Card (PDF)
          </button>
          {/* Iter49 — comparative multi-test report card for this student */}
          <button data-testid="download-report-card" onClick={dlReport} className="nb-btn nb-btn-tertiary">
            <Download size={16}/>Download Report Card
          </button>
        </div>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Exam</th><th>Subject</th><th>Marks</th><th>Max</th></tr></thead>
          <tbody>
            {filteredRows.map((m,i)=>(
              <tr key={`${m.exam}-${m.subject}-${i}`}><td>{m.exam}</td><td>{m.subject}</td>
                <td className="mono font-bold">{m.marks}</td><td className="mono">{m.max_marks}</td></tr>
            ))}
            {filteredRows.length===0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">{rows.length===0 ? "No marks yet" : `No marks for ${testFilter}`}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Fees({ studentId }) {
  const [invs, setInvs] = useState([]);
  const load = ()=> api.get("/fees/invoices", { params: { student_id: studentId }}).then(r=>setInvs(r.data));
  useEffect(()=>{ load(); }, [studentId]);
  const pay = async (invoice_id) => {
    try {
      const r = await api.post("/payments/checkout", { invoice_id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const dl = async (rid) => {
    const r = await api.get(`/fees/receipt/${rid}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return (
    <div className="nb-card p-0 overflow-x-auto">
      <table className="nb-table">
        <thead><tr><th>Term</th><th>Amount</th><th>Due</th><th>Status</th><th>Action</th></tr></thead>
        <tbody>
          {invs.map(i=>(
            <tr key={i.invoice_id} data-testid={`stu-inv-${i.invoice_id}`}>
              <td>{i.term}</td><td className="mono">₹{i.amount}</td><td className="mono">{i.due_date}</td>
              <td><span className={`nb-badge ${i.status==="PAID"?"bg-[#86EFAC]":"bg-[#FCA5A5]"}`}>{i.status}</span></td>
              <td>
                {i.status==="PENDING"
                  ? <button onClick={()=>pay(i.invoice_id)} className="nb-btn"><CreditCard size={16}/>Pay Online</button>
                  : <button onClick={()=>dl(i.receipt_id)} className="nb-btn nb-btn-ghost"><Download size={14}/>Receipt</button>}
              </td>
            </tr>
          ))}
          {invs.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No fee invoices</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Parent({ studentId }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const login = async (e) => {
    e.preventDefault();
    if (!password) return toast.error("Enter your parent password");
    if (!password.endsWith("P")) return toast.error("Parent password must end with 'P' (your child's password + P)");
    setBusy(true);
    try {
      await api.post("/parent/direct-login", { student_id: studentId, password });
      toast.success("Parent portal unlocked");
      window.location.href = "/parent-portal";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    }
    setBusy(false);
  };
  return (
    <div className="nb-card p-8 bg-[#C4B5FD] space-y-6">
      <div>
        <ShieldCheck size={32} className="mb-3"/>
        <h2 className="display text-2xl font-bold">For Parents</h2>
        <p className="mt-2 max-w-2xl text-sm">
          Sign in as a parent directly from this device using your parent password.
          Your parent password is your child&apos;s current password with the letter <b>P</b> appended at the end
          (e.g. if the student password is <span className="mono">Pass0123</span>, the parent password is <span className="mono">Pass0123P</span>).
        </p>
      </div>
      <form onSubmit={login} className="nb-card p-5 bg-white space-y-3 max-w-md" data-testid="parent-direct-form">
        <label className="block">
          <span className="nb-label">Parent Password</span>
          <input type="password" className="nb-input mt-1" value={password}
                 onChange={e=>setPassword(e.target.value)}
                 placeholder="child_password + P"
                 data-testid="parent-direct-password"/>
        </label>
        <button disabled={busy} className="nb-btn w-full justify-center" data-testid="parent-direct-submit">
          {busy ? "Verifying…" : "Enter Parent Portal"}
        </button>
      </form>
      <div className="nb-card-sm p-4 bg-white">
        <div className="nb-label">External access (from a different device)</div>
        <p className="text-sm mt-1">Parents on a separate device (their own phone) can use the OTP flow at the public portal:</p>
        <a href="/parent-access" target="_blank" rel="noreferrer"
           data-testid="parent-portal-link"
           className="nb-btn nb-btn-tertiary mt-3 inline-flex">
          Open OTP Parent Portal
        </a>
      </div>
    </div>
  );
}
